<template>
  <q-list class="bg-white">
    <template v-if="authenticated">
      <q-item
        dense
        class="column justify-center email-row"
      >
        <span class="text-body1 text-center full-width q-my-sm">{{ email }}</span>
      </q-item>
    </template>

    <q-item class="row justify-center">
      <LangSelector />
    </q-item>

    <q-item
      v-if="config.ENABLE_ADVANCED_PROPERTY_INFO"
      class="row"
    >
      <q-item-section side>
        <re-icon
          size="sm"
          name="mdi-tune-variant"
        />
      </q-item-section>

      <q-item-section>
        <span class="text-body1">{{ t('monolith.global.advanced_mode') }}</span>
      </q-item-section>

      <q-item-section side>
        <q-toggle v-model="showAdvanced" />
      </q-item-section>
    </q-item>

    <q-item
      v-if="hasRole('admin')"
      clickable
    >
      <q-item-section side>
        <re-icon
          size="sm"
          name="mdi-shield-account"
        />
      </q-item-section>

      <q-item-section>
        <span class="text-body1">{{ t('monolith.global.roles') }}</span>
      </q-item-section>

      <q-popup-proxy>
        <AdminRoles
          :roles="rolesList"
          :editable="true"
          :unremovable="['-:admin', '*:admin']"
          class="col-auto"
          size="sm"
          @select-role="patch({ roles: listToRoles($event) })"
        />
      </q-popup-proxy>
    </q-item>

    <q-item v-if="enableDevPersonas">
      <re-button
        round
        flat
        icon="mdi-account-switch-outline"
      >
        <q-popup-proxy>
          <DevPersonas class="col-auto" />
        </q-popup-proxy>
      </re-button>
    </q-item>

    <q-item
      v-if="authenticated"
      clickable
      @click="openContactForm"
    >
      <q-item-section side>
        <re-icon
          size="sm"
          name="mdi-face-agent"
        />
      </q-item-section>

      <q-item-section>
        <span class="text-body1">{{ t('monolith.global.support') }}</span>
      </q-item-section>

      <q-item-section side>
        <re-icon
          size="sm"
          name="mdi-arrow-right"
        />
      </q-item-section>
    </q-item>

    <q-item
      v-if="authenticated"
      clickable
      :href="logoutUrl"
      color="primary"
      @click="logout"
    >
      <q-item-section side>
        <re-icon
          size="sm"
          name="mdi-logout-variant"
          color="negative"
        />
      </q-item-section>

      <q-item-section>
        <span class="text-body1 text-negative">
          {{ t('monolith.global.logout') }}
        </span>
      </q-item-section>
    </q-item>
  </q-list>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'

import AdminRoles from '@/apps/MainApp/views/admin/AdminRoles.vue'
import DevPersonas from '@/components/DevPersonas.vue'
import { useAdvancedMode } from '@/composables/useAdvancedMode'
import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import { contactFormKey } from '@/keys'
import utils from '@/plugins/utils'

import LangSelector from './LangSelector.vue'

const { hasRole, roles, email, patch, authenticated, logout } = useCurUser()
const { showAdvanced } = useAdvancedMode()

const contactForm = inject(contactFormKey)
const { t } = useI18n()

const config = useConfig()
const enableDevPersonas = computed(() => config.ENABLE_DEV_FEATURES && config.DEV_PERSONAS)
const logoutUrl = computed(() => utils.urlJoin(config.AUTH_API_URL, 'logout'))
const rolesList = computed(() => utils.rolesToList(roles.value || {}))

function listToRoles(roles: string[]) {
  return utils.listToRoles(roles)
}

function openContactForm() {
  contactForm?.open({
    useLegacy: true,
  })
}
</script>

<style lang="scss" scoped>
.email-row {
  border-bottom: solid $border-light 1px;
}
</style>
